import React from 'react';
import { Table, Input, DatePicker, Button, Space } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { anomalyBusinessDate } from '../../../utils/anomalyBusinessDate';
import { anomalyDocumentType } from '../../../utils/anomalyDocumentType';
import { AnomalyDetail } from '../../../services/pwd216Api';

interface Props {
  details: AnomalyDetail[];
  docNo: string;
  isEditing: boolean;
  onAddRow: (docNo: string) => void;
  onDeleteRow: (docNo: string, lineNo: number) => void;
  onFieldChange: (docNo: string, lineNo: number, field: keyof AnomalyDetail, value: string | number | null) => void;
  onLookup: (docNo: string, lineNo: number, code: string) => void;
}

const Pwd216DetailPanel: React.FC<Props> = React.memo(
  ({ details, docNo, isEditing, onAddRow, onDeleteRow, onFieldChange, onLookup }) => {
    const columns: ColumnsType<AnomalyDetail> = [
      {
        title: <span data-contract-field="ui_detail_line_no">項次</span>,
        dataIndex: 'line_no',
        key: 'line_no',
        width: 60,
        align: 'center',
      },
      {
        title: <span data-contract-field="ui_anomaly_code">異常代號</span>,
        dataIndex: 'anomaly_code',
        key: 'anomaly_code',
        width: 120,
        render: (text: string, record: AnomalyDetail) =>
          isEditing ? (
            <Input
              value={text}
              onChange={(e) => onFieldChange(docNo, record.line_no, 'anomaly_code', e.target.value)}
              onBlur={(e) => onLookup(docNo, record.line_no, e.target.value)}
              size="small"
              placeholder="輸入代號"
            />
          ) : (
            <span style={{ fontFamily: 'monospace' }}>{text}</span>
          ),
      },
      {
        title: <span data-contract-field="ui_anomaly_description">說明</span>,
        dataIndex: 'desc',
        key: 'desc',
        render: (text: string, record: AnomalyDetail) =>
          isEditing ? (
            <Input
              value={text}
              onChange={(e) => onFieldChange(docNo, record.line_no, 'desc', e.target.value)}
              size="small"
            />
          ) : (
            <span>{text}</span>
          ),
      },
      {
        title: <span data-contract-field="ui_scheduled_date">預計作業日期</span>,
        dataIndex: 'sched_date',
        key: 'sched_date',
        width: 150,
        render: (text: string | null, record: AnomalyDetail) =>
          isEditing ? (
            <DatePicker
              value={text ? dayjs(anomalyBusinessDate(text)) : null}
              onChange={(date) =>
                onFieldChange(
                  docNo,
                  record.line_no,
                  'sched_date',
                  date ? `${date.format('YYYY-MM-DD')}T00:00:00+08:00` : null,
                )
              }
              size="small"
              style={{ width: '100%' }}
            />
          ) : (
            <span>{anomalyBusinessDate(text) || '--'}</span>
          ),
      },
      {
        title: <span data-contract-field="ui_generated_doc_type">產生單據類型</span>,
        dataIndex: 'doc_type_code',
        key: 'doc_type_code',
        width: 120,
        render: (_text: string, record: AnomalyDetail) => (
          <span>{anomalyDocumentType(record.seq_no)}</span>
        ),
      },
    ];

    // Add delete column when editing
    if (isEditing) {
      columns.push({
        title: '',
        key: 'action',
        width: 50,
        align: 'center',
        render: (_: unknown, record: AnomalyDetail) => (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => onDeleteRow(docNo, record.line_no)}
          />
        ),
      });
    }

    return (
      <div>
        {isEditing && (
          <div style={{ marginBottom: 8, textAlign: 'right' }}>
            <Space>
              <Button
                size="small"
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => onAddRow(docNo)}
              >
                新增明細
              </Button>
            </Space>
          </div>
        )}
        <Table<AnomalyDetail>
          rowKey="line_no"
          columns={columns}
          dataSource={details}
          size="small"
          pagination={false}
          scroll={{ y: 200 }}
          locale={{ emptyText: '無異常說明明細' }}
        />
      </div>
    );
  },
);

Pwd216DetailPanel.displayName = 'Pwd216DetailPanel';
export default Pwd216DetailPanel;
